import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Navbar } from '../components/Navbar';
import { FamilyReferralLink } from '../components/FamilyReferralLink';
import { PartnerInvitesPanel } from '../components/PartnerInvitesPanel';
import { useAuth } from '../contexts/AuthContext';
import { api, formatApiError } from '../lib/api';
import { toast } from 'sonner';
import { Copy, Users, Building2, DollarSign, RefreshCcw, Link as LinkIcon, CheckCircle2, AlertCircle, ExternalLink, FileText, Upload } from 'lucide-react';
import { TaxFieldsForm, MaskedTaxSummary } from '../components/TaxFieldsForm';

const BUSINESS_TYPE_LABEL = {
  funeral_home: 'Funeral home',
  cemetery: 'Cemetery',
  memorial_business: 'Memorial business',
  other: 'Memorial provider',
};

const STATUS_COPY = {
  pending: {
    title: 'We are reviewing your application',
    body: "Thanks for applying to become a MyLeaf Partner. A member of our team is verifying your details — you'll receive an email once your account is approved (usually within 1–2 business days).",
    tone: 'amber',
  },
  rejected: {
    title: 'We are not able to move forward at this time',
    body: 'Our team has reviewed your application and is unable to approve it right now. If you believe this is a mistake, reply to the email we sent or write to admin@myleaflegacy.info.',
    tone: 'red',
  },
};

function StatusCard({ tone = 'amber', title, body, footer }) {
  const palette = tone === 'red'
    ? 'border-red-400/60 bg-red-50/60 text-red-900'
    : 'border-amber-300/60 bg-amber-50/60 text-amber-900';
  return (
    <div className={`rounded-2xl border ${palette} px-7 py-7 max-w-2xl`}>
      <h2 className="font-serif text-2xl mb-2">{title}</h2>
      <p className="text-sm leading-relaxed opacity-90">{body}</p>
      {footer && <div className="mt-4 text-xs opacity-80">{footer}</div>}
    </div>
  );
}

function MetricTile({ icon: Icon, label, value, testId }) {
  return (
    <div className="rounded-2xl border border-[hsl(var(--primary)/0.18)] bg-[hsl(var(--card))] px-7 py-6" data-testid={testId}>
      <div className="flex items-center gap-2 text-[hsl(var(--primary)/0.6)] text-xs uppercase tracking-wide">
        <Icon size={14} /> {label}
      </div>
      <div className="font-serif text-3xl text-[hsl(var(--primary))] mt-3">{value}</div>
    </div>
  );
}

function StripeBadge({ info }) {
  let label, tone;
  // iter83 — When the platform hasn't activated Connect yet, surface a
  // softer "Manual payouts" badge instead of "Payout setup incomplete"
  // (which suggested the partner had something to do).
  if (info.stripe_connect_enabled === false) {
    label = 'Manual payouts'; tone = 'bg-emerald-50 text-emerald-700 border-emerald-200';
  } else if (!info.stripe_account_id) {
    label = 'Payout setup incomplete'; tone = 'bg-amber-50 text-amber-800 border-amber-200';
  } else if (info.stripe_payouts_enabled) {
    label = 'Payouts active'; tone = 'bg-emerald-50 text-emerald-700 border-emerald-200';
  } else if (info.stripe_details_submitted) {
    label = 'Verification pending'; tone = 'bg-sky-50 text-sky-700 border-sky-200';
  } else {
    label = 'Payout setup incomplete'; tone = 'bg-amber-50 text-amber-800 border-amber-200';
  }
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${tone}`} data-testid="partner-stripe-badge">
      {label}
    </span>
  );
}

export default function PartnerDashboard() {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenLoading, setRegenLoading] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const [w9Loading, setW9Loading] = useState(false);
  const w9InputRef = useRef(null);

  const accountType = info?.account_type;
  const isRegional = accountType === 'regional_partner';
  const isMemorial = accountType === 'memorial_partner';

  const load = useCallback(async () => {
    try {
      // SECURITY: Partners only see aggregate metrics. No PII fetches here —
      // family / memorial / contact information is intentionally never sent
      // to partner accounts by the backend.
      const { data } = await api.get('/partners/me');
      setInfo(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copyInviteCode = async () => {
    if (!info?.partner_invite_code) return;
    try {
      await navigator.clipboard.writeText(info.partner_invite_code);
      toast.success('Invite code copied');
    } catch {
      toast.error('Copy failed — long-press the code to copy manually.');
    }
  };

  const regenInviteCode = async () => {
    if (!window.confirm('Regenerate your invite code? Memorial Partners who haven\'t signed up yet will need the new code.')) return;
    setRegenLoading(true);
    try {
      const { data } = await api.post('/partners/invite-code/generate');
      setInfo((s) => ({ ...s, partner_invite_code: data.partner_invite_code }));
      toast.success('New invite code generated.');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRegenLoading(false);
    }
  };

  const startStripeOnboarding = async () => {
    setConnectLoading(true);
    try {
      const { data } = await api.post('/stripe/connect/onboarding-link', {
        origin_url: window.location.origin,
      });
      // Redirect to Stripe-hosted onboarding.
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setConnectLoading(false);
    }
  };

  const openStripeDashboard = async () => {
    try {
      const { data } = await api.post('/stripe/connect/dashboard-link');
      window.open(data.url, '_blank', 'noopener');
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const refreshStripeStatus = async () => {
    try {
      const { data } = await api.get('/stripe/connect/status');
      setInfo((s) => ({
        ...s,
        stripe_charges_enabled: data.charges_enabled,
        stripe_payouts_enabled: data.payouts_enabled,
        stripe_details_submitted: data.details_submitted,
        stripe_disabled_reason: data.disabled_reason,
      }));
      toast.success('Status refreshed.');
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const uploadW9 = async (file) => {
    if (!file) return;
    setW9Loading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post('/partners/w9', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { data } = await api.get('/partners/me');
      setInfo(data);
      toast.success('W-9 received — thank you.');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setW9Loading(false);
    }
  };

  if (loading || !info) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="ml-container py-20 text-center text-[hsl(var(--primary)/0.6)]" data-testid="partner-dashboard-loading">Loading…</div>
      </div>
    );
  }

  const status = info.status;
  const roleLabel = isRegional ? 'Regional Partner' : 'Memorial Partner';

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="ml-container py-12 sm:py-16 max-w-6xl">
        <header className="mb-10">
          <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--accent))]" data-testid="partner-role-eyebrow">
            {roleLabel}
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[hsl(var(--primary))] mt-2" data-testid="partner-dashboard-title">
            {status === 'approved'
              ? `Welcome, ${(user?.name || '').split(' ')[0] || 'Partner'}`
              : 'Partner application'}
          </h1>
          {info.business_name && (
            <p className="text-[hsl(var(--primary)/0.7)] mt-1" data-testid="partner-business-name">
              {info.business_name}
              {info.business_type && (
                <span className="text-xs uppercase tracking-wider opacity-70 ml-3">
                  · {BUSINESS_TYPE_LABEL[info.business_type] || info.business_type}
                </span>
              )}
            </p>
          )}
        </header>

        {/* PENDING / REJECTED states */}
        {status !== 'approved' && STATUS_COPY[status] && (
          <StatusCard
            tone={STATUS_COPY[status].tone}
            title={STATUS_COPY[status].title}
            body={STATUS_COPY[status].body}
            footer={
              status === 'rejected' && info.rejection_reason ? (
                <span data-testid="partner-rejection-reason">Reason on file: {info.rejection_reason}</span>
              ) : null
            }
          />
        )}

        {/* APPROVED dashboard */}
        {status === 'approved' && (
          <>
            {/* Metric tiles */}
            <section className="grid grid-cols-2 sm:grid-cols-4 gap-5 mb-12" data-testid="partner-metric-grid">
              <MetricTile icon={Users} label="Enrolled families" value={info.summary?.enrolled_families ?? 0} testId="partner-metric-families" />
              {isRegional && (
                <MetricTile icon={Building2} label="Memorial Partners" value={info.summary?.memorial_partners ?? 0} testId="partner-metric-members" />
              )}
              <MetricTile icon={DollarSign} label="Lifetime commissions" value={`$${info.summary?.commissions_total_usd ?? 0}`} testId="partner-metric-total" />
              <MetricTile icon={DollarSign} label="Pending" value={`$${info.summary?.commissions_pending_usd ?? 0}`} testId="partner-metric-pending" />
              <MetricTile icon={DollarSign} label="Paid out" value={`$${info.summary?.commissions_paid_usd ?? 0}`} testId="partner-metric-paid" />
              <MetricTile icon={DollarSign} label="Refunded / reversed" value={`$${info.summary?.commissions_reversed_usd ?? 0}`} testId="partner-metric-reversed" />
            </section>

            {/* iter54 — Signed Partner Agreement copy */}
            {info.agreement_accepted && (
              <section
                className="rounded-2xl border border-[hsl(var(--primary)/0.18)] bg-[hsl(var(--card))] px-6 py-5 mb-6 flex items-start justify-between gap-4 flex-wrap"
                data-testid="partner-agreement-card"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[hsl(var(--accent))] font-semibold">
                    On file
                  </div>
                  <h2 className="font-serif text-lg text-[hsl(var(--primary))] mt-1">
                    Your signed Partner Agreement
                  </h2>
                  <p className="text-[13px] text-[hsl(var(--primary)/0.65)] mt-1">
                    Agreement <span className="font-medium">{info.agreement_version || 'v1.0'}</span>
                    {info.agreement_accepted_at && (
                      <> · accepted {new Date(info.agreement_accepted_at).toLocaleDateString()}</>
                    )}
                    {info.agreement_pdf_emailed_at && (
                      <> · emailed {new Date(info.agreement_pdf_emailed_at).toLocaleDateString()}</>
                    )}
                  </p>
                </div>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/partners/me/agreement.pdf`}
                  target="_blank"
                  rel="noreferrer"
                  className="ml-btn-outline inline-flex items-center gap-2"
                  data-testid="partner-agreement-download-btn"
                >
                  Download signed PDF
                </a>
              </section>
            )}

            {/* Stripe Connect onboarding (Phase 2) */}
            <section className="rounded-2xl border border-[hsl(var(--primary)/0.18)] bg-[hsl(var(--card))] px-8 py-7 mb-8" data-testid="partner-stripe-card">
              <div className="flex items-start justify-between gap-6 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h2 className="font-serif text-xl text-[hsl(var(--primary))]">Payouts via Stripe Connect</h2>
                    <StripeBadge info={info} />
                  </div>
                  <p className="text-sm text-[hsl(var(--primary)/0.7)] mt-1 max-w-2xl">
                    Stripe handles your verification, payouts, and 1099 tax reporting.
                    Onboarding takes about 5 minutes — you'll need your business EIN or SSN, an address, and a bank account.
                  </p>
                  {info.stripe_disabled_reason && (
                    <p className="text-xs text-red-700 mt-2 flex items-center gap-1" data-testid="partner-stripe-disabled">
                      <AlertCircle size={12}/> Stripe needs more info from you: <code>{info.stripe_disabled_reason}</code>
                    </p>
                  )}
                  <p className="text-xs text-[hsl(var(--primary)/0.55)] mt-3" data-testid="partner-stripe-explainer">
                    Payouts are securely handled through Stripe.
                  </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {/* iter83 — When platform-level Stripe Connect is not yet
                      activated (env var missing) OR auto-payouts are off,
                      hide the "Set up payouts" button and show a friendly
                      "Manual payouts — no action needed" badge instead.
                      RPs were getting confused by the Connect error. */}
                  {!info.stripe_connect_enabled ? (
                    <div
                      className="rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-3 text-emerald-800 max-w-md"
                      data-testid="partner-manual-payouts-badge"
                    >
                      <div className="text-[10px] uppercase tracking-widest font-semibold">
                        Manual payouts
                      </div>
                      <div className="text-[13px] mt-1 leading-relaxed">
                        MyLeaf is currently paying partner commissions
                        manually via Zelle, check, or ACH on a recurring
                        cycle. Nothing for you to set up here — we'll reach
                        out by email when payouts are ready.
                      </div>
                    </div>
                  ) : !info.stripe_account_id ? (
                    <button
                      onClick={startStripeOnboarding}
                      disabled={connectLoading}
                      className="ml-btn-gold"
                      data-testid="partner-stripe-connect-btn"
                    >
                      {connectLoading ? 'Opening Stripe…' : 'Set up payouts'}
                    </button>
                  ) : info.stripe_payouts_enabled ? (
                    <>
                      <button onClick={openStripeDashboard} className="ml-btn-gold" data-testid="partner-stripe-dashboard-btn">
                        <ExternalLink size={14}/> Open Stripe dashboard
                      </button>
                      <button onClick={refreshStripeStatus} className="ml-btn-secondary" data-testid="partner-stripe-refresh-btn">
                        <RefreshCcw size={14}/> Refresh
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={startStripeOnboarding} disabled={connectLoading} className="ml-btn-gold" data-testid="partner-stripe-resume-btn">
                        {connectLoading ? 'Opening Stripe…' : 'Resume onboarding'}
                      </button>
                      <button onClick={refreshStripeStatus} className="ml-btn-secondary" data-testid="partner-stripe-refresh-btn">
                        <RefreshCcw size={14}/> Refresh
                      </button>
                    </>
                  )}
                </div>
              </div>
            </section>

            {/* W-9 upload */}
            <section className="rounded-2xl border border-[hsl(var(--primary)/0.18)] bg-[hsl(var(--card))] px-8 py-7 mb-12" data-testid="partner-w9-card">
              <div className="flex items-start justify-between gap-6 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <FileText size={18} className="text-[hsl(var(--primary)/0.7)]"/>
                    <h2 className="font-serif text-xl text-[hsl(var(--primary))]">W-9 on file</h2>
                    {info.w9_uploaded_at && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full" data-testid="partner-w9-status">
                        <CheckCircle2 size={10} className="inline mr-1"/> received
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-[hsl(var(--primary)/0.7)] mt-1 max-w-2xl">
                    Upload a completed W-9 form for our records. Stripe collects its own copy during onboarding — this is our internal back-up.
                  </p>
                  <p className="text-xs text-[hsl(var(--primary)/0.55)] mt-1" data-testid="partner-w9-formats">
                    PDF, PNG, JPG — max 10MB.
                  </p>
                  {info.w9_uploaded_at && info.w9_filename && (
                    <p className="text-xs text-[hsl(var(--primary)/0.55)] mt-2" data-testid="partner-w9-filename">
                      On file: <span className="font-mono">{info.w9_filename}</span> · uploaded {info.w9_uploaded_at.slice(0,10)}
                    </p>
                  )}
                </div>
                <div>
                  <input
                    ref={w9InputRef}
                    type="file"
                    className="hidden"
                    accept="application/pdf,image/png,image/jpeg"
                    onChange={(e) => uploadW9(e.target.files?.[0])}
                    data-testid="partner-w9-input"
                  />
                  <button
                    onClick={() => w9InputRef.current?.click()}
                    disabled={w9Loading}
                    className="ml-btn-secondary"
                    data-testid="partner-w9-upload-btn"
                  >
                    <Upload size={14}/> {w9Loading ? 'Uploading…' : (info.w9_uploaded_at ? 'Replace W-9' : 'Upload W-9')}
                  </button>
                </div>
              </div>
            </section>

            {/* iter94 — Multi-country tax details. Approved partners can
                self-edit; admin-edits flow through a separate endpoint. */}
            <PartnerTaxFieldsCard />

            {/* iter52: Family referral link — every approved partner (RP + MP)
                gets a unique link they can share with families. When a family
                signs up via this link, commissions attach automatically per
                the iter52 structure ($100 direct + $25 RP override when an MP
                refers). Backend enforces attribution — partners cannot
                manipulate it. */}
            {info.partner_invite_code && (
              <FamilyReferralLink
                code={info.partner_invite_code}
                accountType={accountType}
              />
            )}

            {/* Regional Partner — invite code panel (visually prominent) */}
            {isRegional && (
              <section
                className="relative overflow-hidden rounded-3xl border-2 border-[hsl(var(--accent))] bg-gradient-to-br from-[hsl(var(--primary))] to-[#0F1F17] text-[#F4E5C2] px-8 py-9 mb-12 shadow-lg"
                data-testid="partner-invite-card"
              >
                <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[hsl(var(--accent)/0.18)] blur-3xl pointer-events-none" />
                <div className="relative">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[hsl(var(--accent))]"><LinkIcon size={14}/> Memorial Partner invite</div>
                  <h2 className="font-serif text-3xl mt-2 !text-[#F4E5C2]">Your invite code</h2>
                  <p className="text-sm text-[#F4E5C2]/80 mt-2 max-w-2xl leading-relaxed">
                    Share this code with funeral homes, cemeteries, and memorial businesses you bring on.
                    When they apply with this code, their account attaches to yours and commissions split automatically on every family they enrol.
                  </p>
                  <div className="mt-6 flex flex-wrap items-center gap-3">
                    <code
                      className="font-mono text-3xl tracking-[0.5em] bg-[#F4E5C2] text-[hsl(var(--primary))] px-8 py-4 rounded-xl shadow-md font-semibold"
                      data-testid="partner-invite-code-value"
                    >
                      {info.partner_invite_code || '—'}
                    </code>
                    <button onClick={copyInviteCode} className="ml-btn-outline-cream" data-testid="partner-invite-code-copy"><Copy size={14}/> Copy</button>
                    <span
                      className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[hsl(var(--primary))]/55"
                      data-testid="partner-invite-code-permanent"
                      title="Each Regional Partner is issued a permanent 4-digit code. Contact MyLeaf support if your code is compromised."
                    >
                      Permanent code
                    </span>
                  </div>
                </div>
              </section>
            )}

            {/* iter80 — Trackable per-recipient invitations (RP+MP). */}
            <PartnerInvitesPanel isRegional={isRegional} />

            {/* Aggregate counts only — partners NEVER see family PII (names,
                emails, phones, memorial titles, plans, vault state, photos,
                billing details). The backend enforces this; the UI just
                renders what the aggregate-only /partners/me payload returns. */}
            <section className="mb-10" data-testid="partner-aggregate-summary">
              <h2 className="font-serif text-2xl text-[hsl(var(--primary))] mb-2">Your network at a glance</h2>
              <p className="text-sm text-[hsl(var(--primary)/0.6)] mb-5 max-w-2xl leading-relaxed">
                We keep the rest private. To protect families, partners see counts and commission totals only —
                names, contact info, memorial details, and photos are never shared with partner accounts.
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricTile
                  icon={Users}
                  label={isRegional ? 'Direct family enrolments' : 'Family enrolments'}
                  value={info?.summary?.enrolled_families_direct ?? 0}
                  testId="partner-metric-direct-families"
                />
                {isRegional && (
                  <>
                    <MetricTile
                      icon={Building2}
                      label="Memorial Partners invited"
                      value={info?.summary?.memorial_partners ?? 0}
                      testId="partner-metric-memorial-partners"
                    />
                    <MetricTile
                      icon={Users}
                      label="Families via Memorial Partners"
                      value={info?.summary?.enrolled_families_indirect ?? 0}
                      testId="partner-metric-indirect-families"
                    />
                  </>
                )}
                <MetricTile
                  icon={DollarSign}
                  label="Commissions earned (lifetime)"
                  value={`$${info?.summary?.commissions_total_usd ?? 0}`}
                  testId="partner-metric-commissions-total"
                />
              </div>

              <div className="mt-4 grid sm:grid-cols-3 gap-4" data-testid="partner-commission-breakdown">
                <div className="rounded-xl border border-[hsl(var(--primary)/0.12)] bg-[hsl(var(--card))] px-5 py-4">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[hsl(var(--primary)/0.5)]">Paid</div>
                  <div className="font-serif text-2xl text-[hsl(var(--primary))] mt-1">${info?.summary?.commissions_paid_usd ?? 0}</div>
                </div>
                <div className="rounded-xl border border-[hsl(var(--primary)/0.12)] bg-[hsl(var(--card))] px-5 py-4">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[hsl(var(--primary)/0.5)]">Pending payout</div>
                  <div className="font-serif text-2xl text-[hsl(var(--primary))] mt-1">${info?.summary?.commissions_pending_usd ?? 0}</div>
                </div>
                <div className="rounded-xl border border-[hsl(var(--primary)/0.12)] bg-[hsl(var(--card))] px-5 py-4">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[hsl(var(--primary)/0.5)]">Reversed (refunds)</div>
                  <div className="font-serif text-2xl text-[hsl(var(--primary))] mt-1">${info?.summary?.commissions_reversed_usd ?? 0}</div>
                </div>
              </div>
            </section>

            {/* Memorial Partner — surface their parent regional */}
            {isMemorial && info.referred_by_partner_id && (
              <section className="rounded-2xl border border-[hsl(var(--primary)/0.18)] bg-[hsl(var(--card))] px-7 py-6 mb-10" data-testid="partner-parent-card">
                <h2 className="font-serif text-xl text-[hsl(var(--primary))]">Your Regional Partner</h2>
                <p className="text-sm text-[hsl(var(--primary)/0.7)] mt-1">
                  You operate under Regional Partner ID <code className="font-mono text-xs">{info.referred_by_partner_id}</code>. Commissions on every family you enroll are split automatically.
                </p>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}


// iter94 — Tax details card. Partner can view their masked tax info
// and edit it. Sensitive values (full SSN/EIN/SIN/foreign ID) are
// NEVER returned from the backend after save — only last-3/4 digits.
function PartnerTaxFieldsCard() {
  const [masked, setMasked] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/partners/me/tax-fields');
      setMasked(data.tax_fields);
      setForm({
        tax_country: data.tax_fields.tax_country || 'US',
        tax_contractor_type: data.tax_fields.tax_contractor_type || '',
        legal_business_name: data.tax_fields.legal_business_name || '',
        tax_business_number: data.tax_fields.tax_business_number || '',
        tax_corporation_name: data.tax_fields.tax_corporation_name || '',
        tax_business_registration: data.tax_fields.tax_business_registration || '',
        tax_id: '', tax_sin: '', tax_foreign_id: '',
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const k of ['tax_country', 'tax_contractor_type', 'legal_business_name',
                       'tax_business_number', 'tax_corporation_name',
                       'tax_business_registration']) {
        const v = (form[k] || '').toString().trim();
        if (v) payload[k] = v;
      }
      for (const k of ['tax_id', 'tax_sin', 'tax_foreign_id']) {
        if ((form[k] || '').trim()) payload[k] = form[k].trim();
      }
      const { data } = await api.patch('/partners/me/tax-fields', payload);
      if (data?.no_changes) toast.info('No changes to apply.');
      else toast.success(`Saved ${data.changed_count || 0} field change(s).`);
      setEditing(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  return (
    <section
      className="rounded-2xl border border-[hsl(var(--primary)/0.18)] bg-[hsl(var(--card))] px-8 py-7 mb-12"
      data-testid="partner-tax-card"
    >
      <div className="flex items-start justify-between gap-6 flex-wrap mb-5">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <FileText size={18} className="text-[hsl(var(--primary)/0.7)]"/>
            <h2 className="font-serif text-xl text-[hsl(var(--primary))]">Tax details</h2>
          </div>
          <p className="text-sm text-[hsl(var(--primary)/0.7)] mt-1 max-w-2xl">
            Used solely for required regulatory filings (US 1099, Canadian T4A, equivalent). Sensitive IDs are encrypted at rest. Once saved, the full number is never shown again — only the masked last few digits.
          </p>
        </div>
        {!editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="ml-btn-secondary"
            data-testid="partner-tax-edit-btn"
          >
            Edit tax details
          </button>
        )}
      </div>

      {!editing && masked && <MaskedTaxSummary masked={masked} />}

      {editing && (
        <div className="space-y-5">
          <TaxFieldsForm value={form} onChange={setForm} masked={masked} mode="edit" />
          <div className="flex justify-end gap-3 pt-2 border-t border-[hsl(var(--primary)/0.08)]">
            <button
              type="button"
              onClick={() => { setEditing(false); load(); }}
              className="px-5 py-2.5 rounded-full border border-[hsl(var(--primary)/0.18)] text-[hsl(var(--primary))] text-sm"
              data-testid="partner-tax-cancel-btn"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="ml-btn-gold"
              data-testid="partner-tax-save-btn"
            >
              {saving ? 'Saving…' : 'Save tax details'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
